// PROTOTYPE — mock estimate configuration (replaces the Excel config tabs).
// Real API: GET /estimate-config/:versionId returns this whole object.

export const TOOLS = ["Brush", "Edger", "Roller", "Airless Spray", "HVLP"];
export const CONDITIONS = ["New construction", "Repaint", "Restoration"];
export const BRAND_MODES = [
  { value: "MIX", label: "Mix use" },
  { value: "SW", label: "Sherwin-Williams" },
  { value: "BM", label: "Benjamin Moore" },
];
export const SPRAY_TOOLS = ["Airless Spray", "HVLP"];

const globalAssumptions = {
  laborRatePainting: 26.5,
  laborRateFinishing: 28.0,
  overheadPct: 0.1,
  profitPct: 0.15,
  sundriesPct: 0.08,
  bondPct: 0.02,
  subMarkupPct: 0.1,
  salesTaxPct: 0.06,
  hoursPerDay: 8,
  workDaysPerMonth: 22,
  toolWaste: { Brush: 0.05, Edger: 0.06, Roller: 0.1, "Airless Spray": 0.3, HVLP: 0.2 },
  containerSizes: { quart: 0.25, gallon: 1, fiveGal: 5 },
};

const laborBuildup = {
  baseWage: 20.76,
  items: [
    { key: "fica", label: "FICA (Social Security + Medicare)", pct: 0.0765 },
    { key: "futa", label: "FUTA / SUTA", pct: 0.05 },
    { key: "wc", label: "Workers' Comp (painting class 5474)", pct: 0.12 },
    { key: "gl", label: "General Liability", pct: 0.03 },
  ],
};

const accessBands = [
  { id: "GROUND", label: "Ground / ≤9 ft", short: "Ground", maxFt: 9, mult: 1.0 },
  { id: "LADDER", label: "Ladder / 9–16 ft", short: "Ladder", maxFt: 16, mult: 1.1 },
  { id: "LIFT_30", label: "Lift / 16–30 ft", short: "Lift 30", maxFt: 30, mult: 1.2 },
  { id: "LIFT_50", label: "Lift / 30–50 ft", short: "Lift 50", maxFt: 50, mult: 1.35 },
  { id: "BOOM", label: "Boom / 50+ ft", short: "Boom", maxFt: Infinity, mult: 1.5 },
];

const finishingMasking = {
  // Prep hours per 100 SF, by substrate condition.
  conditionPrep: { "New construction": 0.15, Repaint: 0.45, Restoration: 0.9 },
  sspc: [
    { id: "None", label: "None", add: 0 },
    { id: "SP1", label: "SP1 – Solvent clean", add: 0.2 },
    { id: "SP2", label: "SP2 – Hand tool", add: 0.6 },
    { id: "SP3", label: "SP3 – Power tool", add: 1.2 },
    { id: "SP6", label: "SP6 – Commercial blast", add: 2.5 },
    { id: "SP10", label: "SP10 – Near-white blast", add: 3.5 },
  ],
  // Finish level adds skim/sand hours (billed at the finishing rate).
  finishLevels: [
    { id: "—", label: "—", add: 0 },
    { id: "L3", label: "Level 3", add: 0.1 },
    { id: "L4", label: "Level 4", add: 0.2 },
    { id: "L5", label: "Level 5", add: 0.9 },
  ],
  // Masking hours per 100 SF, by field tool.
  maskRates: { Brush: 0.02, Edger: 0.02, Roller: 0.04, "Airless Spray": 0.55, HVLP: 0.35 },
  caulk: { tubesPer100Lf: 4, pricePerTube: 6.5 },
  masking: { tapeSfPerRoll: 1000, tapePrice: 7.2, plasticSfPerRoll: 2000, plasticPrice: 28 },
  finishing: { compoundGalPer100Sf: 0.6, compoundBucketGal: 4.5, compoundBucketPrice: 22, tapeSfPerRoll: 1000, tapeRollPrice: 5 },
  sundriesSplit: [
    { key: "covers", label: "Roller covers & frames", share: 0.3 },
    { key: "brushes", label: "Brushes", share: 0.15 },
    { key: "trays", label: "Trays, liners & buckets", share: 0.1 },
    { key: "cleanup", label: "Rags, cleanup & solvent", share: 0.15 },
    { key: "tips", label: "Spray tips & filters", share: 0.2 },
    { key: "misc", label: "Misc. consumables", share: 0.1 },
  ],
};

// [Brush, Edger, Roller, Airless Spray, HVLP] SF/hr
const S = (id, name, group, unit, sfPerUnit, factor, defaultTool, cutPct, primerRule, finish, primer, coats, prod, extra = {}) => ({
  id, name, group, unit, sfPerUnit, factor, defaultTool, cutPct, primerRule,
  defaultFinishProductId: finish, defaultPrimerProductId: primer, defaultCoats: coats,
  side: group === "Exterior" || id === "metal-ext" ? "EXT" : "INT",
  subbed: false, subRatePerSf: 0, finishLevel: "—",
  productivity: Object.fromEntries(TOOLS.map((t, i) => [t, prod[i]])),
  ...extra,
});

const surfaces = [
  S("walls-gyp", "Walls - Drywall/Gyp", "Interior Walls", "SF", 1, 1.0, "Roller", 0.15, "NEW", "SW-ProMar200-EG", "SW-PVA-Primer", 2, [100, 120, 200, 450, 320], { finishLevel: "L4" }),
  S("ceiling-gyp", "Ceiling - Drywall/Gyp", "Ceilings", "SF", 1, 1.0, "Roller", 0.1, "NEW", "SW-Ceiling-Flat", "SW-PVA-Primer", 2, [100, 120, 200, 450, 320], { finishLevel: "L4" }),
  S("ceiling-textured", "Ceiling - Textured", "Ceilings", "SF", 1, 0.8, "Airless Spray", 0.05, "NEW", "SW-Ceiling-Flat", "SW-PVA-Primer", 1, [90, 105, 170, 380, 270]),
  S("walls-wood", "Walls - Wood", "Interior Walls", "SF", 1, 0.9, "Roller", 0.15, "ALWAYS", "SW-ProClassic-SG", "SW-ProBlock", 2, [95, 110, 190, 420, 290]),
  S("walls-cmu", "Walls - Masonry/CMU", "Interior Walls", "SF", 1, 0.75, "Roller", 0.12, "NEW", "SW-ProMar200-EG", "Block-Filler", 2, [75, 85, 150, 330, 220]),
  S("trim-base", "Trim / Baseboard", "Trim", "LF", 0.5, 1.0, "Brush", 1.0, "NEW", "SW-ProClassic-SG", "SW-ProBlock", 2, [90, 90, 110, 240, 200]),
  S("door-wood", "Door - Wood (2 sides)", "Doors & Frames", "EA", 42, 1.0, "Roller", 0.25, "NEW", "SW-ProClassic-SG", "SW-ProBlock", 2, [60, 65, 95, 220, 180]),
  S("frame-wood", "Door Frame - Wood (w/ casing)", "Doors & Frames", "EA", 17, 1.0, "Brush", 0.7, "NEW", "SW-ProClassic-SG", "SW-ProBlock", 2, [45, 50, 60, 150, 130]),
  S("window-wood", "Window Frame - Wood", "Doors & Frames", "LF", 0.5, 1.0, "Brush", 1.0, "NEW", "SW-ProClassic-SG", "SW-ProBlock", 2, [40, 45, 55, 140, 120]),
  S("cab-paint", "Cabinets - Paint", "Specialty", "LF", 6, 1.0, "HVLP", 0.3, "ALWAYS", "SW-ProClassic-SG", "SW-ExtremeBond", 2, [45, 50, 70, 180, 160]),
  S("cab-stain", "Cabinets - Stain", "Specialty", "LF", 6, 0.9, "HVLP", 0.3, "NEVER", "SW-ProClassic-SG", "", 2, [40, 45, 60, 160, 140]),
  S("metal-int", "Metal - Interior", "Metals", "SF", 1, 1.0, "Brush", 0.4, "ALWAYS", "SW-ProInd-Acrylic-EG", "SW-ProBlock", 2, [70, 75, 110, 300, 220]),
  S("wallcover-l5", "Wall Covering - Level 5", "Interior Walls", "SF", 1, 1.0, "Roller", 0.15, "ALWAYS", "SW-Cashmere-LL", "SW-PVA-Primer", 2, [90, 110, 180, 400, 300], { finishLevel: "L5" }),
  S("ext-stucco", "Exterior - Stucco", "Exterior", "SF", 1, 0.7, "Airless Spray", 0.1, "NEW", "SW-Loxon", "SW-Loxon", 2, [70, 80, 140, 360, 240]),
  S("ext-siding", "Exterior Siding", "Exterior", "SF", 1, 0.9, "Roller", 0.2, "NEVER", "SW-SuperPaint-EG", "", 2, [80, 95, 160, 380, 260]),
  S("soffit-fascia", "Soffit / Fascia", "Exterior", "SF", 1, 0.9, "Airless Spray", 0.3, "NEVER", "SW-SuperPaint-EG", "", 2, [60, 70, 120, 300, 220]),
  S("metal-ext", "Metal - Exterior", "Metals", "SF", 1, 1.0, "Brush", 0.4, "ALWAYS", "SW-ProInd-Acrylic-EG", "SW-ProBlock", 2, [65, 70, 105, 280, 210]),
  S("steel-intumescent", "Heavy Steel - Intumescent", "Specialty", "SF", 1, 1.0, "Airless Spray", 0, "NEVER", "", "", 1, [40, 45, 60, 220, 150], { subbed: true, subRatePerSf: 6.75 }),
  S("epoxy-floor", "Epoxy Floor", "Floors", "SF", 1, 1.0, "Roller", 0.05, "ALWAYS", "SW-ProInd-WB-Epoxy", "SW-ProInd-WB-Epoxy", 2, [70, 80, 140, 0, 0], { subbed: true, subRatePerSf: 4.25 }),
  S("epoxy-cove", "Epoxy Cove Base", "Floors", "LF", 0.5, 1.0, "Brush", 1.0, "ALWAYS", "SW-ProInd-WB-Epoxy", "", 2, [30, 30, 40, 0, 0], { subbed: true, subRatePerSf: 9.5 }),
  S("deck-dryfall", "Steel Deck / Exposed - Dry Fall", "Ceilings", "SF", 1, 1.0, "Airless Spray", 0, "NEVER", "SW-DryFall-Solid", "", 1, [80, 90, 150, 600, 400]),
  S("door-hm", "Door - Steel/HM (2 sides)", "Doors & Frames", "EA", 45, 1.0, "Roller", 0.25, "NEW", "SW-ProInd-Acrylic-EG", "SW-ProBlock", 2, [60, 65, 95, 220, 180]),
  S("frame-hm", "Door Frame - Steel/HM", "Doors & Frames", "EA", 17, 1.0, "Brush", 0.7, "NEW", "SW-ProInd-Acrylic-EG", "SW-ProBlock", 2, [45, 50, 60, 150, 130]),
  S("ext-siding-hardie", "Ext. Siding - Hardie/Vinyl/LP/Cedar", "Exterior", "SF", 1, 0.9, "Roller", 0.15, "NEVER", "SW-SuperPaint-EG", "", 2, [85, 100, 170, 400, 280]),
  S("ext-trim-hardie", "Ext. Trim - Hardie/Vinyl/LP/Cedar", "Exterior", "LF", 0.4, 1.0, "Brush", 0.6, "NEVER", "SW-SuperPaint-EG", "", 2, [70, 75, 95, 220, 180]),
  S("exposed-structure", "Exposed Structure / Open Deck - Int", "Ceilings", "SF", 1, 1.0, "Airless Spray", 0, "NEVER", "SW-DryFall-Solid", "", 1, [75, 85, 140, 550, 380]),
  S("act", "Acoustic Ceiling Tile + Grid (ACT) - Int", "Ceilings", "SF", 1, 0.85, "Airless Spray", 0.02, "NEVER", "SW-Ceiling-Flat", "", 1, [120, 130, 220, 600, 420]),
  S("handrail", "Handrail / Railing / Pipe Rail", "Metals", "LF", 0.8, 1.0, "Brush", 1.0, "ALWAYS", "SW-ProInd-Acrylic-EG", "SW-ProBlock", 2, [35, 40, 50, 140, 110]),
  S("walls-cmu-epoxy", "Walls - CMU Epoxy", "Interior Walls", "SF", 1, 0.75, "Roller", 0.12, "NEW", "SW-ProInd-WB-Epoxy", "Block-Filler", 2, [65, 75, 130, 300, 200]),
  S("walls-gyp-epoxy", "Walls - Gyp Epoxy", "Interior Walls", "SF", 1, 1.0, "Roller", 0.15, "NEW", "SW-ProInd-WB-Epoxy", "SW-PVA-Primer", 2, [85, 100, 170, 400, 280], { finishLevel: "L4" }),
  S("ceiling-gyp-epoxy", "Ceiling - Gyp Epoxy", "Ceilings", "SF", 1, 1.0, "Roller", 0.1, "NEW", "SW-ProInd-WB-Epoxy", "SW-PVA-Primer", 2, [85, 100, 170, 400, 280], { finishLevel: "L4" }),
];

const P = (id, manufacturer, line, sheen, type, coverage, pricePerGal, equivalentId = null) => ({
  id, manufacturer, line, sheen, type, coverage, pricePerGal,
  pricePerQt: +(pricePerGal * 0.32).toFixed(2),
  pricePer5Gal: +(pricePerGal * 4.65).toFixed(2),
  active: true,
  equivalentId,
});

const products = [
  P("SW-ProMar200-EG", "Sherwin-Williams", "ProMar 200 Zero VOC", "Eg-Shel", "FINISH", 350, 14.74, "BM-UltraSpec500-EG"),
  P("SW-ProMar400-EG", "Sherwin-Williams", "ProMar 400 Zero VOC", "Eg-Shel", "FINISH", 350, 12.0),
  P("SW-SuperPaint-EG", "Sherwin-Williams", "SuperPaint Exterior", "Satin", "FINISH", 375, 32),
  P("SW-Cashmere-LL", "Sherwin-Williams", "Cashmere Interior", "Low Lustre", "FINISH", 375, 55, "BM-Regal-Select-EG"),
  P("SW-Emerald-Matte", "Sherwin-Williams", "Emerald Interior", "Matte", "FINISH", 400, 65.79, "BM-ScuffX-Matte"),
  P("SW-ProInd-Acrylic-EG", "Sherwin-Williams", "Pro Industrial Acrylic", "Eg-Shel", "FINISH", 350, 42),
  P("SW-ProInd-WB-Epoxy", "Sherwin-Williams", "Pro Industrial WB Catalyzed Epoxy", "Eg-Shel", "FINISH", 350, 62, "BM-Corotech-WB-Epoxy"),
  P("SW-Ceiling-Flat", "Sherwin-Williams", "ProMar Ceiling", "Flat", "FINISH", 350, 12.5, "BM-UltraSpec-Ceiling-Flat"),
  P("SW-DryFall-Solid", "Sherwin-Williams", "Pro Industrial Waterborne Dryfall", "Flat", "FINISH", 160, 12.78),
  P("SW-ProClassic-SG", "Sherwin-Williams", "ProClassic Interior Acrylic", "Semi-Gloss", "FINISH", 400, 58),
  P("SW-Loxon", "Sherwin-Williams", "Loxon Masonry", "Flat", "FINISH", 200, 50),
  P("SW-PVA-Primer", "Sherwin-Williams", "ProMar 200 PVA Primer", "Flat", "PRIMER", 350, 15),
  P("SW-ProBlock", "Sherwin-Williams", "PrimeRx ProBlock", "Flat", "PRIMER", 350, 24, "BM-FreshStart-Primer"),
  P("SW-ExtremeBond", "Sherwin-Williams", "Extreme Bond Primer", "Flat", "PRIMER", 300, 55),
  P("Block-Filler", "Generic", "Latex Block Filler", "Flat", "PRIMER", 100, 35),
  P("BM-UltraSpec500-EG", "Benjamin Moore", "Ultra Spec 500", "Eggshell", "FINISH", 350, 17.25, "SW-ProMar200-EG"),
  P("BM-Regal-Select-EG", "Benjamin Moore", "Regal Select", "Eggshell", "FINISH", 400, 56.99, "SW-Cashmere-LL"),
  P("BM-ScuffX-Matte", "Benjamin Moore", "Scuff-X", "Matte", "FINISH", 400, 69.5, "SW-Emerald-Matte"),
  P("BM-UltraSpec-Ceiling-Flat", "Benjamin Moore", "Ultra Spec 500 Ceiling", "Flat", "FINISH", 350, 14.1, "SW-Ceiling-Flat"),
  P("BM-Corotech-WB-Epoxy", "Benjamin Moore", "Corotech V440 WB Epoxy", "Eggshell", "FINISH", 340, 66, "SW-ProInd-WB-Epoxy"),
  P("BM-FreshStart-Primer", "Benjamin Moore", "Fresh Start High-Hiding", "Flat", "PRIMER", 350, 27, "SW-ProBlock"),
];

// Paint spec codes from the drawings' finish schedule → product.
const specCodes = {
  "P-1": "SW-ProMar200-EG",
  "P-2": "BM-Regal-Select-EG",
  "P-3": "SW-Emerald-Matte",
  "P-4": "SW-ProInd-Acrylic-EG",
  "P-5": "SW-Ceiling-Flat",
  "P-6": "SW-ProClassic-SG",
  "PCE-1": "SW-ProInd-WB-Epoxy",
  "PCE-2": "SW-ProInd-WB-Epoxy",
  "PCE-3": "SW-ProInd-WB-Epoxy",
  "PCE-5": "SW-ProInd-WB-Epoxy",
  "DF-1": "SW-DryFall-Solid",
  "EXT-1": "SW-SuperPaint-EG",
  "EXT-2": "SW-SuperPaint-EG",
  "EXT-3": "SW-ProInd-Acrylic-EG",
};

// Equipment schedule. `driver` tells the engine where the qty comes from.
const equipmentCatalog = [
  { id: "ladders", item: "Step & extension ladders", basis: "Lump", rate: 180, driver: { band: "LADDER", mode: "used" } },
  { id: "scissor", item: "Scissor lift 19–26 ft", basis: "Week", rate: 425, driver: { band: "LIFT_30", mode: "weeks" } },
  { id: "boom40", item: "Boom lift 40 ft", basis: "Week", rate: 1150, driver: { band: "LIFT_50", mode: "weeks" } },
  { id: "boom60", item: "Boom lift 60 ft", basis: "Week", rate: 1650, driver: { band: "BOOM", mode: "weeks" } },
  { id: "delivery", item: "Lift delivery / pickup", basis: "Each", rate: 175, driver: { mode: "liftTrips" } },
  { id: "airless", item: "Airless sprayer (Graco 695)", basis: "Day", rate: 85, driver: { tool: "Airless Spray", mode: "toolDays" } },
  { id: "hvlp", item: "HVLP finishing system", basis: "Day", rate: 45, driver: { tool: "HVLP", mode: "toolDays" } },
  { id: "drops", item: "Drop cloths & plastic sheeting", basis: "Lump", rate: 220, driver: { mode: "fixed", qty: 1 } },
];

const gcTemplates = [
  {
    id: "gc-default",
    name: "Commercial – Standard",
    items: [
      { id: "foreman", item: "Project Supervision / Foreman", basis: "Day", rate: 360 },
      { id: "mobilization", item: "Mobilization / Demobilization", basis: "Lump", rate: 800 },
      { id: "dumpster", item: "Dumpster", basis: "Each", rate: 450, defaultQty: 2 },
      { id: "temp-power", item: "Temporary Power", basis: "Lump", rate: 350 },
      { id: "conex", item: "Site Storage / Conex", basis: "Month", rate: 175 },
      { id: "toilet", item: "Portable Toilet", basis: "Month", rate: 150 },
      { id: "cleanup", item: "Daily Cleanup", basis: "Day", rate: 25 },
      { id: "permits", item: "Permits", basis: "Lump", rate: 250 },
      { id: "final-clean", item: "Final Cleaning", basis: "Lump", rate: 400 },
    ],
  },
  {
    id: "gc-small",
    name: "Small Repaint – Occupied",
    items: [
      { id: "foreman", item: "Lead Painter (part-time)", basis: "Day", rate: 180 },
      { id: "mobilization", item: "Mobilization / Demobilization", basis: "Lump", rate: 300 },
      { id: "cleanup", item: "Daily Cleanup", basis: "Day", rate: 25 },
    ],
  },
];

export const CONFIG_VERSIONS = [
  { id: "v10", version: 10, label: "Config v10", status: "ARCHIVED", publishedBy: "Admin", publishedAt: "2026-06-02T10:15:00Z", notes: "Initial import from Estimating Workbook 2026.xlsx" },
  { id: "v11", version: 11, label: "Config v11", status: "ARCHIVED", publishedBy: "Admin", publishedAt: "2026-08-14T15:40:00Z", notes: "Updated SW pricing (Q3 price list), workers' comp 11% → 12%" },
  { id: "v12", version: 12, label: "Config v12", status: "ACTIVE", publishedBy: "Admin", publishedAt: "2026-10-01T09:05:00Z", notes: "Added Benjamin Moore equivalents, Hardie siding surfaces" },
];

export const ACTIVE_CONFIG_ID = "v12";

/** @returns the full config for the active version (v12). */
export const buildConfig = (versionId = ACTIVE_CONFIG_ID) => ({
  versionId,
  versionLabel: `${CONFIG_VERSIONS.find((v) => v.id === versionId)?.label || versionId} – Surya Group default`,
  globalAssumptions,
  laborBuildup,
  accessBands,
  finishingMasking,
  surfaces,
  products,
  specCodes,
  equipmentCatalog,
  gcTemplates,
  defaultGcTemplateId: "gc-default",
});
